import api from "@/services/api";
import type {
	RankingEventTypeCount,
	RankingListItem,
	RankingListParams,
	RankingPaginatedResponse,
} from "../types/ranking";

function pickNum(value: unknown): number | undefined {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}
	if (typeof value === "string" && value.trim() !== "") {
		const n = Number(value);
		if (Number.isFinite(n)) {
			return n;
		}
	}
	return;
}

function pickStr(value: unknown): string | null | undefined {
	if (value === null || value === undefined) {
		return value as null | undefined;
	}
	if (typeof value === "string") {
		return value;
	}
	return String(value);
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
	if (!Array.isArray(value)) {
		return [];
	}
	return value.filter(
		(v): v is Record<string, unknown> => v !== null && typeof v === "object"
	);
}

function normalizeEventsByType(value: unknown): RankingEventTypeCount[] {
	return asRecordArray(value)
		.map((entry) => ({
			eventType: pickNum(entry.eventType ?? entry.EventType) ?? 0,
			count: pickNum(entry.count ?? entry.Count) ?? 0,
		}))
		.filter((entry) => entry.eventType > 0);
}

function normalizeRankingIdentity(
	raw: Record<string, unknown>
): Pick<RankingListItem, "name" | "cpf" | "plate" | "model" | "year"> {
	return {
		name: pickStr(raw.name ?? raw.Name),
		cpf: pickStr(raw.cpf ?? raw.Cpf),
		plate: pickStr(raw.plate ?? raw.Plate),
		model: pickStr(raw.model ?? raw.Model),
		year: pickNum(raw.year ?? raw.Year) ?? null,
	};
}

function normalizeRankingMetrics(
	raw: Record<string, unknown>
): Omit<RankingListItem, "name" | "cpf" | "plate" | "model" | "year"> {
	return {
		predominantEventType:
			pickNum(raw.predominantEventType ?? raw.PredominantEventType) ?? null,
		totalEvents: pickNum(raw.totalEvents ?? raw.TotalEvents) ?? 0,
		riskEvents: pickNum(raw.riskEvents ?? raw.RiskEvents) ?? 0,
		riskPercentage: pickNum(raw.riskPercentage ?? raw.RiskPercentage) ?? 0,
		currentOdometer:
			pickNum(raw.currentOdometer ?? raw.CurrentOdometer) ?? null,
		eventsByType: normalizeEventsByType(raw.eventsByType ?? raw.EventsByType),
	};
}

function normalizeRankingItem(raw: Record<string, unknown>): RankingListItem {
	return {
		...normalizeRankingIdentity(raw),
		...normalizeRankingMetrics(raw),
	};
}

function normalizePaginatedPayload(
	data: Record<string, unknown>,
	fallbackParams: RankingListParams
): RankingPaginatedResponse {
	const rawItems =
		data.items ??
		data.Items ??
		data.data ??
		data.Data ??
		data.results ??
		data.Results;

	const itemsRaw = asRecordArray(rawItems);
	const items = itemsRaw.map(normalizeRankingItem);

	const totalCount =
		pickNum(data.totalCount ?? data.TotalCount) ??
		pickNum(data.total ?? data.Total) ??
		items.length;

	const pageSize =
		pickNum(data.pageSize ?? data.PageSize) ?? fallbackParams.pageSize;

	const currentPage =
		pickNum(data.currentPage ?? data.CurrentPage ?? data.page ?? data.Page) ??
		fallbackParams.pageNumber;

	let totalPages = pickNum(data.totalPages ?? data.TotalPages);
	if (totalPages === undefined || totalPages < 1) {
		totalPages = Math.max(1, Math.ceil((totalCount ?? 0) / pageSize));
	}

	return {
		items,
		totalCount: totalCount ?? 0,
		pageSize,
		currentPage,
		totalPages,
	};
}

function buildRankingSearchParams(params: RankingListParams): URLSearchParams {
	const {
		startDate,
		endDate,
		search,
		isDriver,
		pageNumber,
		pageSize,
		sortBy,
		sortDirection,
	} = params;

	const cappedPageSize = Math.min(Math.max(pageSize, 1), 50);

	const searchParams = new URLSearchParams({
		StartDate: startDate,
		EndDate: endDate,
		IsDriver: String(isDriver),
		PageNumber: String(pageNumber),
		PageSize: String(cappedPageSize),
	});

	if (search?.trim()) {
		searchParams.append("Search", search.trim());
	}

	if (sortBy) {
		searchParams.append("SortBy", sortBy);
	}

	if (sortDirection) {
		searchParams.append("SortDirection", sortDirection);
	}

	return searchParams;
}

export async function getRanking(
	params: RankingListParams
): Promise<RankingPaginatedResponse> {
	const searchParams = buildRankingSearchParams(params);

	const { data } = await api.get<unknown>("/api/ranking", {
		params: searchParams,
	});

	if (data !== null && typeof data === "object" && !Array.isArray(data)) {
		return normalizePaginatedPayload(data as Record<string, unknown>, params);
	}

	if (Array.isArray(data)) {
		const items = asRecordArray(data as unknown[]).map(normalizeRankingItem);
		const pageSize = Math.min(Math.max(params.pageSize, 1), 50);
		const totalCount = items.length;
		return {
			items,
			totalCount,
			pageSize,
			currentPage: params.pageNumber,
			totalPages: Math.max(1, Math.ceil(totalCount / pageSize)),
		};
	}

	return {
		items: [],
		totalCount: 0,
		pageSize: Math.min(Math.max(params.pageSize, 1), 50),
		currentPage: params.pageNumber,
		totalPages: 1,
	};
}
